/**
 * 广告命名模板：跨平台共享的编辑器主体（CLAUDE.md §frontend/src/components 可复用组件）。
 *
 * 由 pages/<domain>/AdNameTemplatePage.tsx 以薄壳形式注入域差异：
 *   - pageKey            页面编辑权限 key（PAGE_KEYS.*）
 *   - fetchLatest/save   域 API 服务函数（services/<domain>/adNameTemplate）
 *   - createBlankTemplate 空白模板工厂（各平台默认字段不同）
 *   - allowSecondCombo   是否允许组合 2（缺省 true；华为每个字段只有 1 组枚举，传 false）
 *   - threeComboFields   支持「3 项组合」的字段名集合（腾讯红线特例；华为暂无）
 *   - title/description  编辑器区块标题与说明文案（缺省「广告命名模板」+ 单例说明），
 *                        供一页多组的域（Apple Ads 广告系列/广告组）区分区块
 *   - embedded           嵌入模式（缺省 false）：省略页面级 padding 外壳、标题降为
 *                        区块级字号，供一页多组的域在自己的页面壳内叠放多个编辑器
 *
 * 行为（PR-AT-20260626-004）：
 *   - 进入页面：fetchLatest() 拉取最新模板；始终只存在唯一 template_id='default'，每次保存都是覆盖。
 *   - 字段以「摘要卡」形式默认展示（紧凑 + 灰色底），每张卡可 上移 / 下移 / 编辑 / 删除。
 *   - 点击「编辑」或「+ 新增字段」弹窗中展示对应字段的完整配置。
 *   - 保存：save({ template, remark })。
 *   - 顶部展示已配置模板（field_count / updated_time）。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import type {
  AdNameTemplate,
  AdNameTemplateRecord,
  SaveAdNameTemplateBody,
  TemplateField,
} from "../../types/adNameTemplate.types";
import { FieldSummaryCard } from "./FieldSummaryCard";
import { FieldEditorModal } from "./FieldEditorModal";
import { newField } from "../../utils/adNameTemplateFactory";
import { pushToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import type { PageKey } from "../../config/permissions";

export interface AdNameTemplateEditorProps {
  /** 页面编辑权限 page_key（PAGE_KEYS.*） */
  pageKey: PageKey;
  /** 拉取最新模板（无记录时 resolve null） */
  fetchLatest: () => Promise<AdNameTemplateRecord | null>;
  /** 保存（upsert）模板 */
  save: (body: SaveAdNameTemplateBody) => Promise<AdNameTemplateRecord>;
  /** 构造空白模板（无已保存记录时的编辑起点，各平台默认值不同） */
  createBlankTemplate: () => AdNameTemplate;
  /** 支持「3 项组合」的字段名集合（腾讯红线 get_aom_adname_config combos_cnt=3 特例）；缺省不展示提示 */
  threeComboFields?: ReadonlySet<string>;
  /** 是否允许配置组合 2（缺省 true，腾讯保留「组合 2 可选」；华为每个字段只有 1 组枚举，传 false） */
  allowSecondCombo?: boolean;
  /** 编辑器区块标题（缺省「广告命名模板」；Apple Ads 一页两组时传「广告系列命名模板」等） */
  title?: string;
  /** 标题下的说明文案（缺省单例配置说明；传 null 隐藏） */
  description?: string | null;
  /** 嵌入模式（缺省 false）：省略页面级 padding 外壳、标题降为区块级字号，供一页多组叠放 */
  embedded?: boolean;
}

export function AdNameTemplateEditor({
  pageKey,
  fetchLatest,
  save,
  createBlankTemplate,
  threeComboFields,
  allowSecondCombo = true,
  title = "广告命名模板",
  description = "系统始终只保留唯一一个配置；任何修改保存后即覆盖历史版本。",
  embedded = false,
}: AdNameTemplateEditorProps) {
  const { canEdit } = useAuth();
  const canEditPage = canEdit(pageKey);
  const [template, setTemplate] = useState<AdNameTemplate | null>(null);
  const [loaded, setLoaded] = useState<AdNameTemplateRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [remark, setRemark] = useState("");

  // 弹窗状态：null=关闭；否则为 { idx, mode }（idx=null 表示新增）
  const [modal, setModal] = useState<
    | { idx: number | null; mode: "edit" | "create"; field: TemplateField }
    | null
  >(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLatest();
      if (data) {
        const t: AdNameTemplate = {
          id: data.template_id,
          separator: data.separator,
          fields: data.fields,
        };
        setTemplate(t);
        setLoaded(data);
        setRemark(data.remark ?? "");
      } else {
        const blank = createBlankTemplate();
        setTemplate(blank);
        setLoaded(null);
        setRemark("");
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "拉取模板失败，请稍后重试";
      pushToast(msg, "error");
      setTemplate(createBlankTemplate());
    } finally {
      setLoading(false);
    }
  }, [fetchLatest, createBlankTemplate]);

  useEffect(() => {
    reload();
  }, [reload]);

  // dirty 检测：内容签名比对（template.separator/fields/remark vs loaded）。
  // 保存/重新加载/重置后 template 与 loaded 内容一致 → dirty=false，不高亮
  // （旧实现「任何 template/remark 变化都判脏」会在 保存/重新加载/重置 后误报）。
  const dirty = useMemo(() => {
    if (!loaded || !template) return false;
    return (
      template.separator !== loaded.separator ||
      remark !== (loaded.remark ?? "") ||
      JSON.stringify(template.fields) !== JSON.stringify(loaded.fields)
    );
  }, [template, remark, loaded]);

  function commitField(idx: number | null, next: TemplateField) {
    if (!template) return;
    const fields = [...template.fields];
    if (idx === null) {
      fields.push(next);
    } else {
      fields[idx] = next;
    }
    setTemplate({ ...template, fields });
    setModal(null);
  }

  function deleteField(idx: number) {
    if (!template) return;
    const fields = template.fields.filter((_, i) => i !== idx);
    setTemplate({ ...template, fields });
  }

  function moveField(idx: number, delta: -1 | 1) {
    if (!template) return;
    const target = idx + delta;
    if (target < 0 || target >= template.fields.length) return;
    const fields = [...template.fields];
    const [it] = fields.splice(idx, 1);
    fields.splice(target, 0, it);
    setTemplate({ ...template, fields });
  }

  function openCreate() {
    if (!template) return;
    setModal({
      idx: null,
      mode: "create",
      field: newField(`字段${template.fields.length + 1}`),
    });
  }

  function openEdit(idx: number) {
    if (!template) return;
    setModal({ idx, mode: "edit", field: template.fields[idx] });
  }

  function resetToLoaded() {
    if (loaded) {
      setTemplate({
        id: loaded.template_id,
        separator: loaded.separator,
        fields: loaded.fields,
      });
      setRemark(loaded.remark ?? "");
    } else {
      setTemplate(createBlankTemplate());
      setRemark("");
    }
  }

  async function onSave() {
    if (!template) return;
    if (template.fields.length === 0) {
      pushToast("至少需要 1 个字段组成部分", "error");
      return;
    }
    const hasEmptyName = template.fields.some((f) => !f.name.trim());
    if (hasEmptyName) {
      pushToast("字段名不能为空", "error");
      return;
    }
    for (const f of template.fields) {
      if (f.combos.length < 1 || f.combos.length > 2) {
        pushToast(`字段「${f.name}」的组合组数必须在 1~2 之间`, "error");
        return;
      }
      // 后端规则（PR-AT-20260626-004 第 3 条）前端预校验
      if (f.combos[0].combo_type === "INPUT_BOX" && f.combos.length >= 2) {
        pushToast(`字段「${f.name}」组合 1 为输入框时不允许配置组合 2`, "error");
        return;
      }
      if (f.combos.length >= 2 && f.combos[1].combo_type !== "SINGLE_ENUM") {
        pushToast(`字段「${f.name}」组合 1 为枚举时，组合 2 必须也是枚举`, "error");
        return;
      }
      for (const c of f.combos) {
        if (c.combo_type === "SINGLE_ENUM") {
          const items = c.single_enum_combo?.enum_items ?? [];
          if (items.length === 0) {
            pushToast(`字段「${f.name}」的枚举项不能为空`, "error");
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      // 后端固定使用 template_id='default'，前端无需关心；这里仍传当前 id 以兼容
      const payloadTemplate: AdNameTemplate = {
        id: template.id,
        separator: template.separator,
        fields: template.fields,
      };
      const saved = await save({ template: payloadTemplate, remark: remark.trim() || null });
      pushToast(`保存成功（${saved.template_id}）`, "success");
      setTemplate({
        id: saved.template_id,
        separator: saved.separator,
        fields: saved.fields,
      });
      setLoaded(saved);
      setRemark(saved.remark ?? "");
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存失败";
      pushToast(msg, "error");
    } finally {
      setSaving(false);
    }
  }

  const previewText = useMemo(() => {
    if (!template) return "";
    return previewAdName(template);
  }, [template]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? ""
          : "mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14"
      }
    >
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className={
              embedded
                ? "font-display text-[1.05rem] font-bold leading-tight text-text-emphasis"
                : "font-display text-[1.55rem] font-bold leading-tight text-text-emphasis"
            }
          >
            {title}
          </h1>
          {description !== null ? (
            <p className="mt-1 text-[12.5px] text-text-muted">{description}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {loaded ? (
            <span className="leo-tag is-info">
              当前已配置 · {loaded.field_count} 个字段
            </span>
          ) : (
            <span className="leo-tag is-warning">尚未配置</span>
          )}
          {dirty ? (
            <span className="leo-tag is-warning">有未保存的修改</span>
          ) : null}
        </div>
      </div>

      {!canEditPage ? (
        <div className="mb-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
          当前账号为只读，无法编辑该页面配置。
        </div>
      ) : null}

      {/* 顶部只读概要：分隔符 + 备注；template_id 固定为 'default'，不展示 */}
      <div className="leo-card mb-5 rounded-xl p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-strong">
              字段默认分隔符
            </div>
            <select
              className="leo-select"
              value={template?.separator ?? "-"}
              disabled={!canEditPage}
              onChange={(e) =>
                setTemplate((t) => (t ? { ...t, separator: e.target.value } : t))
              }
            >
              <option value="-">- （横线）</option>
              <option value="_">_ （下划线）</option>
              <option value="">（无）</option>
              <option value=" ">（空格）</option>
            </select>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-strong">
              备注
            </div>
            <input
              type="text"
              className="leo-input"
              placeholder="（可选）"
              value={remark}
              disabled={!canEditPage}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 字段摘要列表（PR-AT-20260626-004 第 5 条） */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[12.5px] text-text-muted">
          共 <span className="text-text-emphasis">{template?.fields.length ?? 0}</span> 个组成部分（按顺序拼接）
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetToLoaded}
            disabled={!dirty || !canEditPage}
            className="leo-btn-ghost"
          >
            重置
          </button>
          <button
            type="button"
            onClick={openCreate}
            disabled={!canEditPage}
            className="leo-btn-ghost"
          >
            + 新增字段
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {template?.fields.map((f, i) => (
          <div key={f.id} className="space-y-1.5">
            <FieldSummaryCard
              index={i}
              field={f}
              onEdit={() => openEdit(i)}
              onDelete={() => deleteField(i)}
              onMoveUp={() => moveField(i, -1)}
              onMoveDown={() => moveField(i, 1)}
              canMoveUp={i > 0}
              canMoveDown={i < (template?.fields.length ?? 0) - 1}
              readOnly={!canEditPage}
            />
            {threeComboFields?.has(f.name) ? (
              <p className="text-[11.5px] leading-relaxed text-text-muted">
                已支持3项组合，如：达人互选%ABRC价%价E。枚举值配置在枚举组1 或 枚举组2 中都可以
              </p>
            ) : null}
          </div>
        ))}
        {template && template.fields.length === 0 ? (
          <div className="leo-card rounded-xl p-8 text-center text-[12.5px] text-text-muted">
            尚未配置任何字段，请点击「+ 新增字段」开始
          </div>
        ) : null}
      </div>

      {/* 预览 + 保存 */}
      <div className="leo-card mt-6 rounded-xl p-5">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[12.5px] font-semibold text-text-emphasis">
            预览拼接示例
          </div>
          <div className="text-[11px] text-text-muted-weak">
            （取每组枚举的第一个枚举值；输入框显示 hint_text）
          </div>
        </div>
        <div className="rounded-md border border-line-subtle bg-da-inset px-3 py-2.5 font-mono text-[12.5px] text-accent-cta-deep">
          {previewText || "（无可预览内容）"}
        </div>
      </div>

      <div className="mt-7 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => reload()}
          disabled={saving}
          className="leo-btn-ghost"
        >
          重新加载
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !canEditPage}
          className="leo-cta-primary w-auto px-7"
        >
          {saving ? "保存中..." : dirty ? "保存模板" : "已保存"}
        </button>
      </div>

      <FieldEditorModal
        open={modal !== null}
        field={modal?.field ?? null}
        mode={modal?.mode ?? "edit"}
        allowSecondCombo={allowSecondCombo}
        onSubmit={(next) => commitField(modal?.idx ?? null, next)}
        onCancel={() => setModal(null)}
      />
    </div>
  );
}

/**
 * 给定模板，按字段顺序拼接预览：每组枚举取第 1 个 item_code；输入框显示 hint_text。
 *
 * 分隔符语义（PR-UI-20260626-001 §1）：
 *   - 组成部分内部：枚举组 1 与枚举组 2 之间以 field.separator_to_next 分隔
 *     （即编辑器中「与下一字段分隔」下拉，实际作用于同一组成部分的两组枚举之间，
 *      而非「当前组成部分 ↔ 下个组成部分」之间；仅 1 组枚举时不生效）。
 *   - 组成部分之间：统一使用全局 template.separator。
 *
 * 仅用于前端预览；保存到 DB 的 fields_json 仍保留全量。
 */
function previewAdName(template: AdNameTemplate): string {
  if (template.fields.length === 0) return "";
  const parts: string[] = [];
  for (let i = 0; i < template.fields.length; i++) {
    const f = template.fields[i];
    const segments: string[] = [];
    for (const combo of f.combos) {
      if (combo.combo_type === "SINGLE_ENUM") {
        const items = combo.single_enum_combo?.enum_items ?? [];
        if (items.length > 0) segments.push(items[0].item_code);
      } else if (combo.combo_type === "INPUT_BOX") {
        const hint = combo.input_box_combo?.hint_text?.trim();
        segments.push(hint ? `[${hint}]` : "[输入框]");
      } else if (combo.combo_type === "DATE") {
        segments.push("[日期]");
      } else if (combo.combo_type === "MULTI_ENUM") {
        segments.push("[多选枚举]");
      }
    }
    // 组成部分内部：枚举组 1 与枚举组 2 之间以 separator_to_next 分隔
    parts.push(segments.filter(Boolean).join(f.separator_to_next ?? ""));
  }
  const result: string[] = [];
  for (let i = 0; i < template.fields.length; i++) {
    if (i > 0) {
      // 组成部分之间：统一使用全局 separator（不再使用 separator_to_next）
      if (template.separator) result.push(template.separator);
    }
    result.push(parts[i]);
  }
  return result.join("");
}
